/**
 * The order one participant sees the statements in.
 *
 * A three-item scale has an order effect: whichever statement is read first is
 * read most carefully, and the two after it are read against it. Presenting
 * every participant with the same order does not remove that — it applies it
 * uniformly and leaves it in the estimate. Varying the order across
 * participants averages it out instead.
 *
 * Two properties make this function rather than a shuffle:
 *
 * - **It is derived from the participant's own identifier**, so the same person
 *   sees the same order in both stages. That is the important one. The whole
 *   output of this survey is `change_N`, a within-subject difference; an order
 *   that changed between the two rounds would be a manipulation applied halfway
 *   through the measurement, and the difference would carry it. Held constant
 *   per participant, an order effect is a constant within each subject and
 *   cancels in the subtraction.
 *
 * - **It is pure and total**, so the export can reconstruct exactly what any
 *   participant was shown without the browser having to report it — which means
 *   the recorded order is a property of the identifier rather than a claim a
 *   client makes about itself.
 *
 * The post-only item is not part of this. It is asked once, after the event,
 * and it is not one of the repeated statements; it always comes last.
 */

/** FNV-1a, 32-bit. Small, fast, and it avalanches well enough to seed a generator. */
function seedFrom(text: string): number {
  let value = 0x811c9dc5;
  for (let index = 0; index < text.length; index++) {
    value ^= text.charCodeAt(index);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

/**
 * mulberry32: one 32-bit state word, no dependencies, identical results in
 * every engine because every step is `Math.imul` or a shift.
 *
 * A cryptographic generator would be the wrong tool. This has to produce the
 * *same* answer on a participant's phone and in the export months later, which
 * is the opposite of what a random source is for.
 */
function generator(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let mixed = state;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The positions of the statements, in the order this participant sees them.
 *
 * Zero-based, and read as a sequence of slots: `[1, 2, 0]` means the second
 * statement was shown first, then the third, then the first. Their answers are
 * still stored against statements one, two and three, because a stored answer
 * is an answer to a statement and not to a position on a screen.
 *
 * `randomize` off returns `[0, 1, ..., count - 1]`, which is the identity and
 * is what every response collected before this existed was shown.
 */
export function statementOrder(participantId: string, count: number, randomize: boolean): number[] {
  const order = Array.from({ length: Math.max(count, 0) }, (_, index) => index);
  if (!randomize) return order;

  const random = generator(seedFrom(participantId));
  // Fisher–Yates, downwards. Every permutation is equally likely given a
  // uniform generator, which is the property that makes this worth doing at
  // all — a biased shuffle would trade one order effect for a subtler one.
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const held = order[i] as number;
    order[i] = order[j] as number;
    order[j] = held;
  }
  return order;
}

/**
 * The same order, written for a spreadsheet cell: one-based, most-significant
 * first, `2|3|1` for "statement two, then three, then one".
 *
 * The separator matches `scale_labels`, which is the only other column that
 * carries a list.
 */
export function formatOrder(order: readonly number[]): string {
  return order.map((position) => position + 1).join('|');
}
